import { Component } from 'react';
import { Svg } from '../Gauge';
import {
   formatLabelIdAndId,
   GAUGE_CENTER_COORDINATES,
   GaugeCoordinates,
   GaugeElementId,
   HALF_CIRCLE_DEGREES,
   QUARTER_CIRCLE_DEGREES
} from '../shared/GaugeShared';

export interface GaugeCircularBarProps {
   svg: Svg;
   svgId: string;
   colors: string[];
   colorPercentages: number[];
}

export class GaugeCircularBar extends Component<GaugeCircularBarProps> {
   private CIRCULAR_BAR_RADIUS = 41;
   private CIRCULAR_BAR_STROKE_WIDTH = 8;
   private CIRCULAR_BAR_BORDER_COLOR = '#000000';
   private CIRCULAR_BAR_LINE_SETTINGS = {
      'stroke-width': 1,
      stroke: this.CIRCULAR_BAR_BORDER_COLOR
   };
   private circularBarSectionsLength = 0;

   constructor(props: GaugeCircularBarProps) {
      super(props);

      this.drawGaugeCircularBarBorder(props);
      this.drawGaugeCircularBarSections(props);
   }

   render(): null {
      return null;
   }

   UNSAFE_componentWillReceiveProps(nextProps: GaugeCircularBarProps): void {
      this.clearCircularSections(nextProps.svg, nextProps.svgId);
      this.drawGaugeCircularBarSections(nextProps);
   }

   drawGaugeCircularBarBorder = (props: GaugeCircularBarProps): void => {
      this.drawGaugeCircularBarTopLineBorder(props);
      this.drawGaugeCircularBarBottomLineBorder(props);
      this.drawGaugeCircularBarCircularBorder(props);
   };

   drawGaugeCircularBarTopLineBorder = (props: GaugeCircularBarProps): void => {
      props.svg.draw.line(GAUGE_CENTER_COORDINATES.x, 4.5, GAUGE_CENTER_COORDINATES.y, 13.5)
         .attr(this.CIRCULAR_BAR_LINE_SETTINGS);
   };

   drawGaugeCircularBarBottomLineBorder = (props: GaugeCircularBarProps): void => {
      props.svg.draw.line(GAUGE_CENTER_COORDINATES.x, 86.5, GAUGE_CENTER_COORDINATES.y, 95.5)
         .attr(this.CIRCULAR_BAR_LINE_SETTINGS);
   };

   drawGaugeCircularBarCircularBorder = (props: GaugeCircularBarProps): void => {
      const pathOptions: string = this.describeArc(GAUGE_CENTER_COORDINATES.x,
                                                   GAUGE_CENTER_COORDINATES.y,
                                                   this.CIRCULAR_BAR_RADIUS,
                                                   0,
                                                   HALF_CIRCLE_DEGREES);

      props.svg.draw.path(pathOptions)
         .attr({
                  fill: 'none',
                  'stroke-width': this.CIRCULAR_BAR_STROKE_WIDTH + 1,
                  stroke: this.CIRCULAR_BAR_BORDER_COLOR
               });
   };

   drawGaugeCircularBarSections = (props: GaugeCircularBarProps): void => {
      let startAngle = 0;
      let endAngle = 0;
      let pathOptions = '';

      props.colorPercentages.map((value: number, index: number): void => {
         endAngle += value * HALF_CIRCLE_DEGREES;
         pathOptions = this.describeArc(GAUGE_CENTER_COORDINATES.x,
                                        GAUGE_CENTER_COORDINATES.y,
                                        this.CIRCULAR_BAR_RADIUS,
                                        startAngle,
                                        endAngle);

         props.svg.draw.path(pathOptions)
            .attr({
                     id: formatLabelIdAndId(GaugeElementId.CIRCULAR_BAR_SECTION, `${props.svgId}-${index}`),
                     fill: 'none',
                     'stroke-width': this.CIRCULAR_BAR_STROKE_WIDTH,
                     stroke: props.colors[index]
                  });

         startAngle = endAngle;
      });

      this.circularBarSectionsLength = props.colorPercentages.length;
   };

   describeArc = (x: number,
                  y: number,
                  radius: number,
                  startAngle: number,
                  endAngle: number): string => {
      const start: GaugeCoordinates = this.polarToCartesian(x, y, radius, endAngle);
      const end: GaugeCoordinates = this.polarToCartesian(x, y, radius, startAngle);
      const largeArcFlag: string = endAngle - startAngle <= HALF_CIRCLE_DEGREES ? '0' : '1';

      return [
         'M', start.x, start.y,
         'A', radius, radius, 0, largeArcFlag, 0, end.x, end.y
      ].join(' ');
   };

   polarToCartesian = (centerX: number,
                       centerY: number,
                       radius: number,
                       angleInDegrees: number): GaugeCoordinates => {
      const angleInRadians: number = (angleInDegrees - QUARTER_CIRCLE_DEGREES) * Math.PI / HALF_CIRCLE_DEGREES;

      return {
         x: centerX + (radius * Math.cos(angleInRadians)),
         y: centerY + (radius * Math.sin(angleInRadians))
      };
   };

   clearCircularSections = (svg: Svg, svgId: string): void => {
      for (let i = 0; i < this.circularBarSectionsLength; i++) {
         svg.get(formatLabelIdAndId(GaugeElementId.CIRCULAR_BAR_SECTION, `${svgId}-${i}`)).remove();
      }
   };
}
